import React, { useCallback, useEffect, useRef, useState } from 'react';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page, Toolbar } from '@syncfusion/ej2-react-grids';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { Modal, Button } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import { maintenancesService } from '../../Services/maintenances.service';
import { ToastComponent } from '../Components/ToastComponent';
import Spinner from '../Components/Spinner';
import { useTranslation } from 'react-i18next';

export const ItensMaintenances = () => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    dispatch({ type: "title", value: "Cadastro de itens" });
  }, [dispatch]);

  const [itens, setItens] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [editId, setEditId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [toastParams, setToastParams] = useState('');
  const [dataLoading, setDataLoading] = useState(true);
  const [pageSize, setPageSize] = useState(15);

  const gridRef = useRef(null);

  const ROW_HEIGHT = 30;
  const HEADER_PAGER_BUDGET = 100;  // cabeçalho + pager da grid
  const BOTTOM_MARGIN = 30;         // sem margem ao fim
  const MIN_PAGE = 5;
  const MAX_PAGE = 100;

  const calculateGridMetrics = () => {
    
    const winH = window.innerHeight;
    const top = (gridRef.current && gridRef.current.getBoundingClientRect().top) || 0;

    const available = Math.max(50, winH - top - BOTTOM_MARGIN);
    const rowsArea = Math.max(50, available - HEADER_PAGER_BUDGET);
    const rowsFit = Math.floor(rowsArea / ROW_HEIGHT);

    // total de itens (para este grid é o mesmo do dataSource do componente)
    const total = Array.isArray(itens) ? itens.length : 0;

    // se couber tudo, mostra tudo (e evita 2ª página)
    const targetRows = Math.max(MIN_PAGE, Math.min(rowsFit, total, MAX_PAGE));

    setPageSize(targetRows);
  };

  useEffect(() => {
    
    let timeoutId = null;

    const handleResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(calculateGridMetrics, 50);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [calculateGridMetrics]);


  const handleSave = async () => {
    
    if (newItem.trim() !== "") {
      try {
        const payload = {
          maintenanceItemId: editId ?? 0,
          maintenanceItemName: newItem,
          active: "S"
        }

        const response = await maintenancesService.createNewItem(payload);

        if (response) {
          if (editId) {
            setToastParams(JSON.stringify({
              show: true,
              title: "Sucesso",
              body: 'Item de manutenção atualizado com sucesso.',
              color: 'success'
            }));
            // Atualiza item na lista
            setItens(itens.map(item =>
              item.maintenanceItemId === editId
                ? { ...item, maintenanceItemName: newItem }
                : item
            ));
            setEditId(null);
          } else {
            setToastParams(JSON.stringify({
              show: true,
              title: "Sucesso",
              body: 'Item de manutenção criado com sucesso.',
              color: 'success'
            }));
            setItens([...itens, payload]);
          }
        } else {
          setToastParams(JSON.stringify({
            show: true,
            title: "Erro",
            body: 'Não foi possível processar o item.',
            color: 'danger'
          }));
        }
      } catch (error) {
        console.error("Erro:", error);
        setToastParams(JSON.stringify({
          show: true,
          title: "Erro",
          body: 'Ocorreu um erro ao processar a requisição.',
          color: 'danger'
        }));
      }
      setNewItem("");
    }
  };

  const handleEdit = (id) => {
    const itemToEdit = itens.find((item) => item.maintenanceItemId === id);
    if (itemToEdit) {
      setNewItem(itemToEdit.maintenanceItemName);
      setEditId(id);
    }
  };

  const handleClear = () => {
    setNewItem("");
    setEditId(null);
  };


  const handleDelete = async () => {
    try {
      const itemToDelete = itens.find((item) => item.maintenanceItemId === deleteId);

      if (itemToDelete) {
        const payload = {
          maintenanceItemId: deleteId,
          maintenanceItemName: itemToDelete.maintenanceItemName,
          active: "N"
        };

        const response = await maintenancesService.createNewItem(payload);

        if (response) {
          setToastParams(JSON.stringify({
            show: true,
            title: "Sucesso",
            body: 'Item de manutenção deletado com sucesso.',
            color: 'success'
          }));

          // Remove da lista local
          setItens(itens.filter((item) => item.maintenanceItemId !== deleteId));
        } else {
          setToastParams(JSON.stringify({
            show: true,
            title: "Erro",
            body: 'Não foi possível deletar o item.',
            color: 'danger'
          }));
        }
      }
    } catch (error) {
      console.error("Erro ao deletar:", error);
      setToastParams(JSON.stringify({
        show: true,
        title: "Erro",
        body: 'Ocorreu um erro ao deletar.',
        color: 'danger'
      }));
    } finally {
      setShowModal(false);
      setDeleteId(null);
    }
  };


  const actionTemplateEdit = (props) => (
    <button
      className="btn btn-link p-0"
      style={{ color: "var(--text-primary)" }}
      onClick={() => handleEdit(props.maintenanceItemId)}
    >
      <FaEdit size={18} />
    </button>
  );

  const actionTemplateDelete = (props) => (
    <button
      className="btn btn-link p-0"
      style={{ color: "var(--text-primary)" }}
      onClick={() => {
        setDeleteId(props.maintenanceItemId);
        setShowModal(true);
      }}
    >
      <FaTrash size={18} />
    </button>
  );

  const fetchItens = async () => {
    setDataLoading(true)
    try {
      const response = await maintenancesService.fetchItemsMaintenace()

      if (response) {
        setItens(response)
      } else {
        setItens([])
      }
    } catch (error) {
      console.error("Erro ao buscar itens de manutenção:", error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    fetchItens();
  }, []);



  return (
    <>
      <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />

      <div className='ml-4'>
        <p>{t("MAINTENANCES.MAINTENANCE_ITEM.DESCRIPTION")}</p>
        <div className="filter-maintenance">
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            placeholder="Digite o nome do item"
            className="form-control-maintenance"
          />
          <button onClick={handleSave} className="btn btn-primary me-2">
            {editId ? t("MAINTENANCES.MAINTENANCE_ITEM.FIELD_TABLE_EDIT") : t("MAINTENANCES.MAINTENANCE_ITEM.BTN_SAVE")}
          </button>
          <button onClick={handleClear} className="btn btn-secondary">
            {t("MAINTENANCES.MAINTENANCE_ITEM.BTN_CANCEL")}
          </button>
        </div>

        {/* GridComponent para exibir os itens */}
        <div className="card mt-4" ref={gridRef}>
          {dataLoading ? (
            <Spinner />
          ) : (
            <GridComponent
              dataSource={itens}
              allowPaging={true}
              pageSettings={{ pageSize: pageSize }}
              rowHeight={ROW_HEIGHT}                // <- importantíssimo pra bater com o cálculo
              height={"auto"}                      // altura justa, sem "sobras"
            >
              <ColumnsDirective>
                <ColumnDirective field="maintenanceItemName" headerText={t("MAINTENANCES.MAINTENANCE_ITEM.FIELD_TABLE_ITEM")} width="200" />
                <ColumnDirective
                  headerText={t("MAINTENANCES.MAINTENANCE_ITEM.FIELD_TABLE_EDIT")}
                  width="100"
                  textAlign="Center"
                  template={actionTemplateEdit}
                />
                <ColumnDirective
                  headerText={t("MAINTENANCES.MAINTENANCE_ITEM.FIELD_TABLE_DELETE")}
                  width="100"
                  textAlign="Center"
                  template={actionTemplateDelete}
                />
              </ColumnsDirective>
              <Inject services={[Page]} />
            </GridComponent>
          )}
        </div>

        {/* Modal de confirmação para exclusão */}
        <Modal show={showModal} onHide={() => setShowModal(false)} centered>
          <Modal.Body>{t("DELETE_MODAL.CONFIRM_DELETE")}</Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              {t("MAINTENANCES.MAINTENANCE_ITEM.BTN_CANCEL")}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              {t("MAINTENANCES.MAINTENANCE_ITEM.BTN_SAVE")}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>

    </>
  );
};
